// =============================================================================
// Chunk-load recovery — turns a dead screen into a reload, but only when a
// reload can actually fix it.
//
// Why this exists
// ---------------
// `next build` emits content-hashed chunks. A deploy replaces the container,
// so any chunk whose content changed is gone from the server under its old
// name. A page loaded from the previous build keeps those old names in memory
// and asks for them later — on route navigation, and (critically) on every
// `dynamic()` import, which is how the order sheet loads. Webpack's jsonp
// loader tries exactly ONCE and then rejects with ChunkLoadError. It never
// retries. Measured on prod: a deploy invalidates 3–51 of 224 chunks, and
// `main-app-*` changes on every single one.
//
// The same ChunkLoadError is also produced by a plain network failure — a
// stalled radio on 2g/3g, which is the common case for this user base (18 of
// the 19 events in the 4-day sample were network, not 404).
//
// Those two causes need OPPOSITE responses:
//   • stale build  → a reload fetches the new document and fixes it for good
//   • device offline → a reload strands the user on a blank page and destroys
//                      whatever they had on screen
//
// So we never reload on the error alone. We probe the failed chunk URL first
// and only reload when the probe proves the server is reachable.
//
// Safety rules (this is a trading app — an unwanted reload can eat a
// half-typed order):
//   1. Only chunk-load failures ever trigger this. Nothing else.
//   2. Never while a critical operation is registered (order sheet open,
//      request in flight). We show a dismissible prompt instead.
//   3. At most ONE automatic reload per build. The counter is keyed by
//      release SHA, so it resets once a reload actually lands on a new build
//      — that bounds loops without permanently disabling recovery.
//   4. If the probe says the device is offline, we do nothing automatic. The
//      user retries by hand, exactly as they do today for connection loss.
// =============================================================================

/** Release SHA baked in at build time. Identifies which build this tab runs. */
const RELEASE = process.env.NEXT_PUBLIC_RELEASE_SHA || "unknown"

/** sessionStorage key holding `{ release, n }` — the per-build reload counter. */
const STORAGE_KEY = "__chunk_reload_v1"

/** At most one silent reload per build. See rule 3 above. */
const MAX_AUTO_RELOADS = 1

/** Per-attempt probe budget. Long enough for a slow-but-alive link, short
 *  enough to leave room for a retry inside the total budget below. */
const PROBE_ATTEMPT_TIMEOUT_MS = 3000

/**
 * Total wall-clock the probe may spend before we give up and call it offline.
 *
 * Retries exist because prod says they should. Of the 14 chunk incidents in the
 * 2026-08-13→15 sample, every failing chunk still served 200 from the origin
 * afterwards: they were stalled radios on 2g/4g, not missing files. A single
 * attempt turns a transient stall into a dead screen the user must clear by
 * hand; a second attempt a moment later is the cheapest thing that can turn it
 * back into a silent recovery.
 *
 * The budget is what keeps that honest — the user is watching a spinner while
 * we decide, so the answer has to arrive in seconds, not tens of seconds.
 */
const PROBE_TOTAL_BUDGET_MS = 8000

/** Pause between probe attempts. Long enough to outlast a momentary stall,
 *  short enough that two retries still fit inside the total budget. */
const PROBE_RETRY_DELAY_MS = 600

/**
 * Hard cap on attempts, independent of the time budget.
 *
 * The budget alone does not bound this: against an unreachable host `fetch`
 * rejects immediately (DNS / connection refused) rather than burning its
 * timeout, so a purely time-bounded loop would fire ~13 requests in 8 s. Three
 * is what the inline boot guard does too — the two layers must agree, or one
 * of them is wrong about what "we tried" means.
 */
const PROBE_MAX_ATTEMPTS = 3

export type ChunkFailureKind =
  /** Server answered 404 — this chunk belongs to a build that no longer exists. */
  | "stale-build"
  /** Server answered 2xx — the file is there; the earlier load stalled transiently. */
  | "reachable"
  /** Probe could not complete — device is offline or the origin is unreachable. */
  | "offline"

export type RecoveryOutcome =
  /** Reload was triggered; the document is on its way out. */
  | "reloading"
  /** Reachable, but a critical operation is in flight — caller should prompt. */
  | "deferred-critical"
  /** Already auto-reloaded once on this build — caller should prompt. */
  | "deferred-exhausted"
  /** Device is offline — caller should show connection UI with a manual retry. */
  | "offline"
  /** Not a chunk error; caller should fall through to its normal error UI. */
  | "not-chunk-error"

// ─── Detection ───────────────────────────────────────────────────────────────

// Webpack's jsonp loader throws `ChunkLoadError` with a message shaped
//   "Loading chunk 542 failed.\n(timeout: https://host/_next/static/chunks/…)"
// The `(error:` variant covers both a 404 and a dropped connection, which is
// exactly why the probe below exists. The remaining patterns cover native
// ESM dynamic imports (Turbopack, and Safari's wording), so this keeps
// working if the bundler changes underneath us.
const CHUNK_MESSAGE_PATTERNS = [
  /Loading chunk [\w.-]+ failed/i,
  /Loading CSS chunk [\w.-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
]

/** True when `err` is a chunk/dynamic-import load failure from any bundler. */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const name = (err as { name?: unknown })?.name
  if (typeof name === "string" && name === "ChunkLoadError") return true
  const message = (err as { message?: unknown })?.message
  const text = typeof message === "string" ? message : typeof err === "string" ? err : ""
  if (!text) return false
  return CHUNK_MESSAGE_PATTERNS.some((re) => re.test(text))
}

/**
 * Pull the chunk URL out of the error so we can probe it.
 *
 * Webpack puts it in the message as `(timeout: <url>)` / `(error: <url>)`.
 * We fall back to the first same-origin `/_next/static/` URL anywhere in the
 * message or stack, which covers wordings we haven't seen yet.
 */
export function extractChunkUrl(err: unknown): string | null {
  const parts: string[] = []
  const message = (err as { message?: unknown })?.message
  if (typeof message === "string") parts.push(message)
  const stack = (err as { stack?: unknown })?.stack
  if (typeof stack === "string") parts.push(stack)
  if (typeof err === "string") parts.push(err)
  const haystack = parts.join("\n")
  if (!haystack) return null

  const parenthesised = haystack.match(/\((?:timeout|error|missing):\s*(https?:\/\/[^\s)]+)\)/i)
  if (parenthesised?.[1]) return parenthesised[1]

  const anyChunk = haystack.match(/https?:\/\/[^\s")']+\/_next\/static\/[^\s")']+/i)
  if (!anyChunk?.[0]) return null
  // Stack frames carry a `:<line>:<col>` suffix ("…/542.abc.js:1:3587"). Probing
  // that verbatim would request a path that never exists and misreport every
  // failure as "stale-build" — the one classification that triggers a reload.
  return anyChunk[0].replace(/(\.[a-z]+):\d+(?::\d+)?$/i, "$1")
}

// ─── Critical-operation registry ─────────────────────────────────────────────
//
// Anything that would lose user work or money if the document went away
// registers here for its lifetime. While the count is above zero we never
// reload automatically — the caller surfaces a prompt and the user decides.
//
// Mirrored onto `window` so the inline <head> boot guard (layout.tsx), which
// runs before any module graph exists, can read the same signal.

let criticalDepth = 0

declare global {
  interface Window {
    __chunkCriticalDepth?: number
  }
}

function syncCriticalToWindow(): void {
  if (typeof window === "undefined") return
  window.__chunkCriticalDepth = criticalDepth
}

/**
 * Mark the start of an operation that must not be interrupted by a reload.
 * Returns the matching release function — call it in a `finally` or a React
 * cleanup. Re-entrant: nested calls are counted, not overwritten.
 */
export function beginCriticalOperation(): () => void {
  criticalDepth += 1
  syncCriticalToWindow()
  let released = false
  return function endCriticalOperation() {
    // Idempotent: React StrictMode / double-invoked cleanups must not drive
    // the counter negative and silently unlock the guard.
    if (released) return
    released = true
    criticalDepth = Math.max(0, criticalDepth - 1)
    syncCriticalToWindow()
  }
}

/** True while any critical operation is registered. */
export function isCriticalOperationActive(): boolean {
  return criticalDepth > 0
}

/** Test seam — resets module state between cases. */
export function __resetCriticalOperations(): void {
  criticalDepth = 0
  syncCriticalToWindow()
}

// ─── Per-build reload counter ────────────────────────────────────────────────

interface ReloadState {
  release: string
  n: number
}

function readReloadState(): ReloadState {
  const fresh: ReloadState = { release: RELEASE, n: 0 }
  if (typeof window === "undefined") return fresh
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return fresh
    const parsed = JSON.parse(raw) as Partial<ReloadState>
    // A different release means the previous reload DID land on a new build —
    // recovery worked, so the budget resets. Same release means the reload
    // changed nothing and we must not go round again.
    if (parsed?.release !== RELEASE) return fresh
    return { release: RELEASE, n: typeof parsed.n === "number" ? parsed.n : 0 }
  } catch {
    // sessionStorage unavailable (Safari private mode, sandboxed iframe).
    // Fail CLOSED: without a durable counter we cannot bound a reload loop.
    return { release: RELEASE, n: MAX_AUTO_RELOADS }
  }
}

function bumpReloadState(): void {
  if (typeof window === "undefined") return
  try {
    const next = readReloadState()
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ release: RELEASE, n: next.n + 1 } satisfies ReloadState)
    )
  } catch {
    /* best-effort — the fail-closed read above already bounds the loop */
  }
}

/** True when an automatic reload is still within budget for this build. */
export function canAutoReload(): boolean {
  return readReloadState().n < MAX_AUTO_RELOADS
}

// ─── Probe ───────────────────────────────────────────────────────────────────

/**
 * Ask the server about the chunk that failed. This is the whole basis for the
 * reload decision — a ChunkLoadError alone cannot tell us whether the file is
 * gone or the radio dropped.
 *
 * `cache: "no-store"` is essential: a cached 200 from the pre-deploy document
 * would tell us the old chunk still exists when it does not.
 */
export async function classifyChunkFailure(
  url: string,
  now: () => number = Date.now
): Promise<ChunkFailureKind> {
  if (typeof fetch !== "function") return "offline"

  const deadline = now() + PROBE_TOTAL_BUDGET_MS

  for (let attempt = 0; attempt < PROBE_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      const left = deadline - now()
      if (left <= PROBE_RETRY_DELAY_MS) return "offline"
      await sleep(PROBE_RETRY_DELAY_MS)
    }

    const remaining = deadline - now()
    if (remaining <= 0) return "offline"

    try {
      const res = await fetch(url, {
        method: "HEAD",
        cache: "no-store",
        credentials: "omit",
        signal: AbortSignal.timeout(Math.min(PROBE_ATTEMPT_TIMEOUT_MS, remaining)),
      })
      if (res.status === 404 || res.status === 410) return "stale-build"
      if (res.ok) return "reachable"
      // 5xx / 429 — the origin is up but unhappy, and it answered, so this is
      // not the transient-radio case a retry fixes. Reloading into a struggling
      // server helps nobody: treat it as not-recoverable-right-now and stop.
      return "offline"
    } catch {
      // Network error, DNS failure, or our own per-attempt abort. Nothing was
      // proven, so try again while the budget lasts — this is the stalled-radio
      // case that makes up the bulk of real chunk failures.
    }
  }

  // Attempts exhausted without a single answer from the origin.
  return "offline"
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ─── Recovery ────────────────────────────────────────────────────────────────

interface RecoverOptions {
  /** Injected in tests. Defaults to a real page reload. */
  reload?: () => void
  /** Injected in tests. Defaults to `classifyChunkFailure`. */
  classify?: (url: string) => Promise<ChunkFailureKind>
}

/**
 * Decide what to do about a chunk-load failure and, when it is both safe and
 * useful, reload the page.
 *
 * Returns the outcome so the caller can render the right thing: "reloading"
 * means the document is going away and the caller should show a brief
 * updating state; every other value means the caller owns the UI.
 */
export async function recoverFromChunkError(
  err: unknown,
  opts: RecoverOptions = {}
): Promise<RecoveryOutcome> {
  if (!isChunkLoadError(err)) return "not-chunk-error"

  const url = extractChunkUrl(err)
  // No URL to probe means no evidence. Reloading blind could strand an
  // offline user, so we hand control to the caller's manual-retry UI.
  if (!url) return "offline"

  const classify = opts.classify ?? classifyChunkFailure
  const kind = await classify(url)
  if (kind === "offline") return "offline"

  // Server is reachable — a reload will genuinely help. Now the safety gates.
  if (isCriticalOperationActive()) return "deferred-critical"
  if (!canAutoReload()) return "deferred-exhausted"

  bumpReloadState()
  const reload = opts.reload ?? (() => window.location.reload())
  reload()
  return "reloading"
}

/**
 * User-initiated retry, for the "Try Again" button on the offline screen.
 *
 * Deliberately NOT `recoverFromChunkError`: that function encodes what we are
 * allowed to do BEHIND the user's back, and every one of its gates exists to
 * protect them from a reload they did not ask for. Here they asked. So this
 * probes and reports, and the caller reloads — no budget spend, no silent
 * reload, and no reload at all while the link is still dead (which is what
 * pressing "Try Again" used to do: tear down the page and land on the offline
 * shell with the trader's screen gone).
 */
export async function retryChunkProbe(
  err: unknown,
  opts: Pick<RecoverOptions, "classify"> = {}
): Promise<ChunkFailureKind> {
  const url = extractChunkUrl(err)
  if (!url) return "offline"
  const classify = opts.classify ?? classifyChunkFailure
  return classify(url)
}
